import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BuyGuideCardComponent } from '@app/guides/buy-guide-card/buy-guide-card.component';
import { ASSET_PATHS } from '@app/constants/asset-paths';

interface BuyerGuideResource {
  title: string;
  description: string;
  link: string;
  linkLabel: string;
}

interface BuyerGuideTip {
  title: string;
  description: string;
}

@Component({
  standalone: true,
  imports: [BuyGuideCardComponent, RouterLink],
  selector: 'app-buyer-guide',
  templateUrl: './buyer-guide.component.html',
  styleUrls: ['./buyer-guide.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyerGuideComponent {
  readonly assetPaths = ASSET_PATHS;

  readonly highlights = [
    'Local agents who know Laredo neighborhoods',
    'Map search across homes, land, and commercial',
    'Guidance from pre-approval through closing',
  ];

  readonly budgetItems = [
    {
      title: 'Down payment',
      detail: 'Typically 3–20% of the purchase price, depending on your loan program.',
    },
    {
      title: 'Closing costs',
      detail: 'Often 2–5% of the loan amount for title, lender fees, and prepaid items.',
    },
    {
      title: 'Earnest money & option fee',
      detail: 'Texas contracts commonly include an option period — budget for the fee and refundable earnest money.',
    },
    {
      title: 'Inspections & appraisal',
      detail: 'Home inspections, surveys, and lender appraisals protect you before closing.',
    },
    {
      title: 'Insurance & taxes',
      detail: 'Homeowners insurance and property taxes are part of your ongoing monthly cost.',
    },
  ];

  readonly resources: BuyerGuideResource[] = [
    {
      title: 'Interactive Map Search',
      description:
        'Draw a boundary on the map or filter by price, beds, and property type to zero in on listings in the areas you want.',
      link: '/featured-properties',
      linkLabel: 'Browse listings',
    },
    {
      title: 'Meet Our Agents',
      description:
        'Get matched with a Gabali agent who specializes in the neighborhoods and property types you are considering.',
      link: '/agents',
      linkLabel: 'View agents',
    },
    {
      title: 'Save Favorite Listings',
      description:
        'Create an account to save homes, compare options, and pick up your search right where you left off.',
      link: '/favorite-properties',
      linkLabel: 'View favorites',
    },
    {
      title: 'Free Home Estimate',
      description:
        'Curious what homes are selling for nearby? Request a quick market snapshot before you start touring.',
      link: '/home-estimate-page',
      linkLabel: 'Get an estimate',
    },
  ];

  readonly localTips: BuyerGuideTip[] = [
    {
      title: 'North vs. central Laredo',
      description:
        'Newer subdivisions and larger lots tend to cluster north of Loop 20, while central areas offer shorter commutes and established amenities.',
    },
    {
      title: 'School districts matter',
      description:
        'Families often filter by UISD and LISD boundaries early — your agent can help align listings with the schools you are targeting.',
    },
    {
      title: 'New build vs. resale',
      description:
        'Builders may cover closing costs or offer upgrades, but resale homes can offer faster move-in timelines and mature landscaping.',
    },
    {
      title: 'Borderplex market dynamics',
      description:
        'Cross-border commerce and regional growth influence demand in Laredo. Pricing can vary block by block, so local comps matter.',
    },
  ];
}
